import Phaser from "phaser";
import { ModalLayer, NINE_SLICE_BUTTON_EVENT, NineSliceButton, NineSlicePanel } from "@yjsgames/phaser-ui";
import { CONTENT_PANEL_STYLE, FONT_FAMILY, RARITY_COLOR, SMALL_BUTTON_STYLE, TYPO, hexToNum, rarityLabel } from "./uiTokens.js";
import { PALETTE, PALETTE_NUM } from "../themes/palette.js";
import { t, getTextDirection } from "../i18n/i18n.js";
import { popupCloseButtonX } from "./directionalUi.js";
import { setExpandedCloseButtonHitArea } from "./closeButtonHitArea.js";
import { modulePresentationIconKey } from "./modulePresentationIcon.js";
import { drawSynergyIcon } from "./synergyIcon.js";
import {
  SYNERGY_INFO_ICON_SIZE,
  SYNERGY_INFO_PANEL_H,
  SYNERGY_INFO_PANEL_W,
  SYNERGY_INFO_TARGET_CARD_H,
  SYNERGY_INFO_TARGET_CARD_W,
  SYNERGY_INFO_TARGET_ICON_SIZE,
  computeSynergyInfoPopupLayout,
} from "./synergyInfoPopupLayout.js";

export interface SynergyInfoData {
  readonly id: string;
  readonly name: string;
  readonly grade: string;
  readonly level: number;
  readonly requiredModuleIds: readonly string[];
  readonly requiredModuleNames: readonly string[];
  readonly currentEffect: string;
  readonly nextEffect: string;
}

export interface SynergyInfoPopupOptions {
  readonly screenWidth: number;
  readonly screenHeight: number;
  readonly onClose: () => void;
  readonly onOpenModule?: (definitionId: string) => void;
  readonly onHighlightPipes?: (synergyId: string) => void;
}

const SYNERGY_GRADE_COLOR: Record<string, string> = {
  common: RARITY_COLOR.common,
  advanced: RARITY_COLOR.rare,
  rare: RARITY_COLOR.epic,
  legendary: RARITY_COLOR.legendary,
};
const HIGHLIGHT_TRANSPARENT_HOLD_MS = 3000;
const HIGHLIGHT_RESTORE_FADE_MS = 180;
const HIGHLIGHT_INPUT_BLOCKER_DEPTH = 900_000;

/**
 * 시너지 정보 팝업 — 시너지 자체 아이콘에서 열리는 전용 상세.
 * 조건 파이프와 현재/다음 효과를 보여주며 대상 파이프 아이콘은 모듈 정보 팝업으로 이어진다.
 * 전투탭에서 열린 경우에만 파이프 위치 강조 버튼을 제공한다.
 */
export class SynergyInfoPopup extends ModalLayer {
  private readonly opts: SynergyInfoPopupOptions;
  private readonly cx: number;
  private readonly top: number;
  private readonly glyph: Phaser.GameObjects.Graphics;
  private readonly title: Phaser.GameObjects.Text;
  private readonly gradeText: Phaser.GameObjects.Text;
  private readonly conditionLabel: Phaser.GameObjects.Text;
  private readonly effectLabel: Phaser.GameObjects.Text;
  private readonly effectText: Phaser.GameObjects.Text;
  private readonly highlightButton?: NineSliceButton;
  private readonly highlightInputBlocker: Phaser.GameObjects.Zone;
  private readonly targetObjects: Phaser.GameObjects.GameObject[] = [];
  private currentData: SynergyInfoData | null = null;
  private highlightFadeTween?: Phaser.Tweens.Tween;
  private highlightRestoreTimer?: Phaser.Time.TimerEvent;
  private highlightClickRestoreTimer?: Phaser.Time.TimerEvent;
  private highlightBlockerReleaseTimer?: Phaser.Time.TimerEvent;
  private highlightOpacityActive = false;

  constructor(scene: Phaser.Scene, opts: SynergyInfoPopupOptions) {
    let restoreFromBackdropClick: (() => boolean) | undefined;
    super(scene, {
      screenWidth: opts.screenWidth,
      screenHeight: opts.screenHeight,
      overlayColor: PALETTE_NUM.overlayDim,
      overlayAlpha: 0.82,
      onBackdropClick: () => {
        if (restoreFromBackdropClick?.()) return;
        opts.onClose();
      },
    });
    restoreFromBackdropClick = () => this.restoreHighlightOpacityIfActive();
    this.opts = opts;
    this.cx = opts.screenWidth / 2;
    const cy = opts.screenHeight / 2;
    this.top = cy - SYNERGY_INFO_PANEL_H / 2;
    const layout = computeSynergyInfoPopupLayout(this.cx, this.top, 1);
    this.setBackdropCloseExclusion(layout.panel.x, layout.panel.y, layout.panel.width, layout.panel.height);

    const panel = new NineSlicePanel(scene, this.cx, cy, {
      style: CONTENT_PANEL_STYLE,
      width: SYNERGY_INFO_PANEL_W,
      height: SYNERGY_INFO_PANEL_H,
      originX: 0.5,
      originY: 0.5,
    });
    this.contentContainer.add(panel);

    const closeBtn = scene.add
      .text(popupCloseButtonX(this.cx, SYNERGY_INFO_PANEL_W, 18, getTextDirection()), this.top + 16, "✕", {
        fontFamily: FONT_FAMILY,
        fontSize: TYPO.display,
        color: PALETTE.textSecondary,
      })
      .setOrigin(0.5);
    setExpandedCloseButtonHitArea(closeBtn);
    closeBtn.on("pointerup", () => this.opts.onClose());

    this.glyph = scene.add.graphics().setPosition(
      layout.glyph.x + layout.glyph.width / 2,
      layout.glyph.y + layout.glyph.height / 2,
    );
    this.title = scene.add
      .text(this.cx, layout.title.y, "", {
        fontFamily: FONT_FAMILY,
        fontSize: TYPO.title,
        color: PALETTE.textBright,
        align: "center",
        wordWrap: { width: layout.title.width },
      })
      .setOrigin(0.5, 0);
    this.gradeText = scene.add
      .text(this.cx, layout.grade.y + layout.grade.height / 2, "", { fontFamily: FONT_FAMILY, fontSize: TYPO.body, color: PALETTE.textDim })
      .setOrigin(0.5);
    this.conditionLabel = scene.add
      .text(this.cx, layout.conditionLabel.y + layout.conditionLabel.height / 2, t("synergyInfo.condition"), {
        fontFamily: FONT_FAMILY,
        fontSize: TYPO.caption,
        color: PALETTE.textSecondary,
      })
      .setOrigin(0.5);
    this.effectLabel = scene.add
      .text(this.cx, layout.effectLabel.y + layout.effectLabel.height / 2, t("synergyInfo.effect"), {
        fontFamily: FONT_FAMILY,
        fontSize: TYPO.caption,
        color: PALETTE.textSecondary,
      })
      .setOrigin(0.5);
    this.effectText = scene.add
      .text(this.cx, layout.effectText.y, "", {
        fontFamily: FONT_FAMILY,
        fontSize: TYPO.body,
        color: PALETTE.textBright,
        align: "center",
        wordWrap: { width: layout.effectText.width },
      })
      .setOrigin(0.5, 0);
    if (opts.onHighlightPipes) {
      const button = new NineSliceButton(
        scene,
        layout.highlightButton.x + layout.highlightButton.width / 2,
        layout.highlightButton.y + layout.highlightButton.height / 2,
        {
          style: SMALL_BUTTON_STYLE,
          label: t("synergyInfo.highlightPipes"),
          widthOverride: layout.highlightButton.width,
          heightOverride: layout.highlightButton.height,
        },
      );
      button.setDepth(4);
      button.on(NINE_SLICE_BUTTON_EVENT.Click, () => {
        const id = this.currentData?.id;
        if (id) this.flashTransparentForHighlight(id);
      });
      this.highlightButton = button;
    }
    this.contentContainer.add([
      this.title,
      closeBtn,
      this.glyph,
      this.gradeText,
      this.conditionLabel,
      this.effectLabel,
      this.effectText,
    ]);
    if (this.highlightButton) this.contentContainer.add(this.highlightButton);

    this.highlightInputBlocker = scene.add
      .zone(opts.screenWidth / 2, opts.screenHeight / 2, opts.screenWidth, opts.screenHeight)
      .setDepth(HIGHLIGHT_INPUT_BLOCKER_DEPTH)
      .setActive(false);
    this.highlightInputBlocker.name = "synergyHighlightInputBlocker";
    this.highlightInputBlocker.disableInteractive();
    this.highlightInputBlocker.on(
      "pointerdown",
      (
        _pointer: Phaser.Input.Pointer,
        _localX: number,
        _localY: number,
        event?: Phaser.Types.Input.EventData,
      ) => {
        event?.stopPropagation();
        this.restoreHighlightOpacity({ keepBlockerUntilPointerUp: true });
      },
    );
    this.highlightInputBlocker.on(
      "pointerup",
      (
        _pointer: Phaser.Input.Pointer,
        _localX: number,
        _localY: number,
        event?: Phaser.Types.Input.EventData,
      ) => {
        event?.stopPropagation();
        this.disableHighlightInputBlockerNextTick();
      },
    );
  }

  update(data: SynergyInfoData): void {
    this.currentData = data;
    const color = SYNERGY_GRADE_COLOR[data.grade] ?? PALETTE.textBright;
    const colorNum = hexToNum(color);
    this.title.setText(data.name).setColor(color);
    this.gradeText.setText(t("synergyInfo.levelGrade", { level: data.level, grade: rarityLabel(data.grade) }));
    drawSynergyIcon(this.glyph, SYNERGY_INFO_ICON_SIZE, colorNum, PALETTE_NUM.cellBg, 1);
    this.effectText.setText(
      t("synergyInfo.effectBody", {
        current: data.currentEffect,
        next: data.nextEffect,
      }),
    );
    this.rebuildTargets(data, colorNum);
  }

  applyLanguage(): void {
    this.conditionLabel.setText(t("synergyInfo.condition"));
    this.effectLabel.setText(t("synergyInfo.effect"));
    this.highlightButton?.setLabel(t("synergyInfo.highlightPipes"));
    if (this.currentData) this.update(this.currentData);
  }

  public override destroy(fromScene?: boolean): void {
    this.clearHighlightOpacityState();
    this.highlightInputBlocker.destroy();
    super.destroy(fromScene);
  }

  private flashTransparentForHighlight(synergyId: string): void {
    this.opts.onHighlightPipes?.(synergyId);
    this.clearHighlightOpacityState();
    this.highlightOpacityActive = true;
    this.setAlpha(0);
    this.highlightClickRestoreTimer = this.scene.time.delayedCall(0, () => {
      if (!this.active) return;
      this.setHighlightInputBlockerEnabled(true);
    });
    this.highlightRestoreTimer = this.scene.time.delayedCall(HIGHLIGHT_TRANSPARENT_HOLD_MS, () => {
      if (!this.active) return;
      this.highlightFadeTween = this.scene.tweens.add({
        targets: this,
        alpha: 1,
        duration: HIGHLIGHT_RESTORE_FADE_MS,
        ease: "Sine.easeOut",
        onComplete: () => {
          this.highlightOpacityActive = false;
          this.highlightFadeTween = undefined;
          this.highlightRestoreTimer = undefined;
          this.highlightClickRestoreTimer = undefined;
          this.setHighlightInputBlockerEnabled(false);
        },
      });
    });
  }

  private restoreHighlightOpacity(opts: { keepBlockerUntilPointerUp?: boolean } = {}): void {
    this.restoreHighlightOpacityIfActive(opts);
  }

  private restoreHighlightOpacityIfActive(opts: { keepBlockerUntilPointerUp?: boolean } = {}): boolean {
    const wasActive = this.highlightOpacityActive;
    this.clearHighlightOpacityState({ keepBlocker: opts.keepBlockerUntilPointerUp });
    if (!wasActive || !this.active) return wasActive;
    this.setAlpha(1);
    return true;
  }

  private clearHighlightOpacityState(opts: { keepBlocker?: boolean } = {}): void {
    this.highlightFadeTween?.stop();
    this.highlightRestoreTimer?.remove(false);
    this.highlightClickRestoreTimer?.remove(false);
    this.highlightBlockerReleaseTimer?.remove(false);
    this.highlightOpacityActive = false;
    this.highlightFadeTween = undefined;
    this.highlightRestoreTimer = undefined;
    this.highlightClickRestoreTimer = undefined;
    this.highlightBlockerReleaseTimer = undefined;
    if (!opts.keepBlocker) this.setHighlightInputBlockerEnabled(false);
  }

  private setHighlightInputBlockerEnabled(enabled: boolean): void {
    this.highlightBlockerReleaseTimer?.remove(false);
    this.highlightBlockerReleaseTimer = undefined;
    if (enabled) {
      this.highlightInputBlocker
        .setPosition(this.opts.screenWidth / 2, this.opts.screenHeight / 2)
        .setSize(this.opts.screenWidth, this.opts.screenHeight)
        .setDepth(HIGHLIGHT_INPUT_BLOCKER_DEPTH)
        .setActive(true)
        .setInteractive();
      this.scene.children.bringToTop(this.highlightInputBlocker);
      return;
    }
    this.highlightInputBlocker.disableInteractive();
    this.highlightInputBlocker.setActive(false);
  }

  private disableHighlightInputBlockerNextTick(): void {
    this.highlightBlockerReleaseTimer?.remove(false);
    this.highlightBlockerReleaseTimer = this.scene.time.delayedCall(0, () => {
      if (!this.active || this.highlightOpacityActive) return;
      this.setHighlightInputBlockerEnabled(false);
    });
  }

  private rebuildTargets(data: SynergyInfoData, color: number): void {
    for (const obj of this.targetObjects) obj.destroy();
    this.targetObjects.length = 0;
    const layout = computeSynergyInfoPopupLayout(this.cx, this.top, data.requiredModuleIds.length);
    data.requiredModuleIds.forEach((moduleId, i) => {
      const card = layout.targetCards[i]!;
      const x = card.x + card.width / 2;
      const y = card.y + card.height / 2;
      const bg = this.scene.add
        .rectangle(x, y, SYNERGY_INFO_TARGET_CARD_W, SYNERGY_INFO_TARGET_CARD_H, PALETTE_NUM.cellBg, 0.72)
        .setStrokeStyle(1, color, 0.45)
        .setInteractive({ useHandCursor: true });
      const icon = this.scene.add
        .image(x, y - 9, modulePresentationIconKey(moduleId))
        .setDisplaySize(SYNERGY_INFO_TARGET_ICON_SIZE, SYNERGY_INFO_TARGET_ICON_SIZE)
        .setTint(color);
      const label = this.scene.add
        .text(x, y + 21, data.requiredModuleNames[i] ?? moduleId, {
          fontFamily: FONT_FAMILY,
          fontSize: TYPO.micro,
          color: PALETTE.textSecondary,
          align: "center",
          wordWrap: { width: SYNERGY_INFO_TARGET_CARD_W - 8 },
        })
        .setOrigin(0.5, 0.5);
      bg.on("pointerup", () => this.opts.onOpenModule?.(moduleId));
      this.contentContainer.add([bg, icon, label]);
      this.targetObjects.push(bg, icon, label);
    });
  }
}
